import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, Check, ArrowRight, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useRegion } from '@/context/RegionContext';
import { PLAN_PRICES } from '@/lib/plans';
import AppNav from '@/components/AppNav';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import CurrencyToggle, { PriceShownNote } from '@/components/CurrencyToggle';
import FreeTranscriber from '@/components/FreeTranscriber';
import HowItWorks from '@/components/HowItWorks';
import HomeToolkitSections from '@/components/HomeToolkitSections';

/*
 * HomePage — Feb 2026 redesign (based on Fable handoff, adapted to Poettik's
 * existing Tailwind theme tokens). Uses the app's cyan (--primary) + pink
 * (--accent) palette rather than hardcoded hex, so it stays consistent with
 * the rest of the app.
 *
 * Fonts: Archivo (display), Inter (body — the app default), JetBrains Mono
 * (eyebrows / timecodes). Font-face imports live in /public/index.html.
 *
 * `lockedRegion` — when set to 'us' or 'uk' by /us or /uk landing routes we
 * hide the currency toggle and stick the region on every register CTA so the
 * new user's profile is region-locked from day one.
 */

// Rotating hero hooks — typed one character at a time to hint at the product
// without the visitor needing to sign up.
const HERO_HOOKS = [
  'Everyone with a car in the UK needs to hear this…',
  'I was today years old when I found out this exists',
  'POV: you never get caught out by a mobile camera again',
  'My dad laughed at this gadget. He\u2019s not laughing now.',
];

function useTypedHooks() {
  const [lines, setLines] = useState(['']);
  const [done, setDone] = useState(false);
  useEffect(() => {
    // Respect the visitor's reduced-motion preference — show everything at once.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setLines([...HERO_HOOKS]); setDone(true); return; }

    let li = 0;   // index of the hook we're currently typing
    let ci = 0;   // number of characters revealed so far
    let cancelled = false;
    let timeoutId;

    // Compute the visible-lines array from scratch each tick — depending on
    // `prev` inside setLines was fragile (undefined on double-mount in dev).
    const render = () => {
      const next = HERO_HOOKS.slice(0, li);         // fully-typed lines
      if (li < HERO_HOOKS.length) {
        next[li] = HERO_HOOKS[li].slice(0, ci);      // currently-typing line
      }
      setLines(next.length ? next : ['']);
    };

    const tick = () => {
      if (cancelled) return;
      if (li >= HERO_HOOKS.length) { setDone(true); return; }
      ci += 1;
      render();
      if (ci >= HERO_HOOKS[li].length) {
        li += 1;
        ci = 0;
        timeoutId = setTimeout(tick, 420);
      } else {
        timeoutId = setTimeout(tick, 22);
      }
    };

    timeoutId = setTimeout(tick, 700);
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);
  return { lines, done };
}

/* ── shared design tokens (Tailwind class strings) ─────────────────────── */

const eyebrowClass = 'font-mono text-[11px] sm:text-xs tracking-[0.16em] uppercase text-accent';

const HeadingXL = ({ children, accent, className = '' }) => (
  <h2 className={`font-display leading-[1.05] tracking-[-0.02em] text-3xl sm:text-4xl lg:text-5xl text-foreground ${className}`}>
    {children}
    {accent && <span className="italic text-primary"> {accent}</span>}
  </h2>
);

const HomePage = ({ lockedRegion = null }) => {
  const { isAuthenticated } = useAuth();
  const { formatPrice, setRegion, region } = useRegion();
  const [searchParams] = useSearchParams();
  const { lines: heroLines, done: heroDone } = useTypedHooks();

  // When the visitor lands on /us or /uk, lock the region context so pricing
  // and currency stay consistent through the funnel.
  useEffect(() => {
    if (lockedRegion === 'us' || lockedRegion === 'uk') {
      setRegion(lockedRegion, { persistToServer: false });
    }
  }, [lockedRegion, setRegion]);

  // Referral code capture — kept from the original HomePage for the affiliate
  // programme.
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      localStorage.setItem('kylo_ref_code', ref.toUpperCase());
    }
  }, [searchParams]);

  // Smooth-scroll to a section when the URL carries a hash (e.g. the nav's
  // Features dropdown links like /#top-products), on load and on hash change.
  useEffect(() => {
    const scrollToHash = () => {
      const id = decodeURIComponent(window.location.hash.replace('#', ''));
      if (!id) return;
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          const y = el.getBoundingClientRect().top + window.scrollY - 20;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 80);
      }
    };
    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);


  const registerHref = lockedRegion ? `/register?region=${lockedRegion}` : '/register';
  const pricingHref = lockedRegion ? `/${lockedRegion}/pricing` : '/pricing';

  const registerWithPlan = (plan) => {
    const params = new URLSearchParams();
    if (plan) params.set('plan', plan);
    if (lockedRegion) params.set('region', lockedRegion);
    const qs = params.toString();
    return `/register${qs ? `?${qs}` : ''}`;
  };

  const plans = useMemo(() => ([
    {
      key: 'trial',
      name: 'Free Trial',
      priceLabel: formatPrice(PLAN_PRICES.trial),
      per: '3 days',
      cta: 'Start free trial',
      href: registerHref,
      hot: false,
      feats: [
        'Full hook sets for 3 products',
        'Includes a full 30–60s script',
        'Viral Script Transcriber included',
        'No card required',
      ],
    },
    {
      key: 'basic',
      name: 'Basic',
      priceLabel: formatPrice(PLAN_PRICES.basic),
      per: 'month',
      cta: isAuthenticated ? 'View plans' : 'Get Basic',
      href: isAuthenticated ? pricingHref : registerWithPlan('basic'),
      hot: false,
      feats: [
        '80 generations / month',
        'Hooks, scripts, filming ideas',
        'Viral Script Transcriber',
        'Save your idea library',
      ],
    },
    {
      key: 'pro',
      name: 'Pro',
      priceLabel: formatPrice(PLAN_PRICES.pro),
      per: 'month',
      cta: isAuthenticated ? 'View plans' : 'Go Pro',
      href: isAuthenticated ? pricingHref : registerWithPlan('pro'),
      hot: true,
      feats: [
        '200 generations / month',
        'Re-spin hooks for fresh angles',
        '5 text hooks (vs 3 on Basic)',
        'Priority AI processing',
        'Early access to new features',
      ],
    },
  ]), [formatPrice, isAuthenticated, lockedRegion, registerHref, pricingHref]);

  return (
    <div className="min-h-screen bg-background text-foreground font-body">
      <SEO
        title="Poettik — AI hooks, angles & scripts for TikTok Shop and UGC creators"
        description="Turn any product into scroll-stopping hooks, angles and a 30–60s script — ready to film. Built by a TikTok Shop creator, not a boardroom."
        canonical={lockedRegion ? `https://poettik.com/${lockedRegion}` : 'https://poettik.com/'}
      />
      <nav className="relative z-50 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16">
          <AppNav />
        </div>
      </nav>

      {/* ═══ HERO ═════════════════════════════════════════════════════════ */}
      <header className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-16 sm:pb-24">
          <div className="max-w-3xl mx-auto text-center">
            {/* Copy + free transcriber */}
            <div className="flex flex-col items-center">
              <p className={eyebrowClass} data-testid="hero-eyebrow">
                For TikTok Shop affiliates &amp; UGC creators
              </p>
              <h1 className="font-display tracking-[-0.02em] leading-[1.05] text-4xl sm:text-5xl lg:text-6xl mt-4 mb-5">
                Staring at a product,<br />
                no idea what to <span className="hl">say?</span><br />
                <span className="italic text-primary">Paste it in.</span>
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-[560px] mx-auto mb-7">
                Paste a winning TikTok or Instagram URL below and we&rsquo;ll transcribe it instantly.
                Then spin it into a script for your own product &mdash; and we&rsquo;ll even generate a voiceover to go with it.
              </p>

              <div id="transcriber" className="w-full scroll-mt-24">
                <FreeTranscriber />
              </div>

              <div
                className="w-full max-w-2xl mx-auto mt-10 rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/[0.06] to-accent/[0.05] p-5 sm:p-6 text-left shadow-[0_14px_40px_rgba(70,45,20,0.07)]"
                data-testid="hero-violation-banner"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                  <div className="flex items-start gap-3 flex-1">
                    <span className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 text-primary">
                      <ShieldCheck className="w-6 h-6" />
                    </span>
                    <div>
                      <p className="font-display text-lg sm:text-xl tracking-[-0.01em] leading-tight">
                        Got a video or account flagged?
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                        Our free Violation Wizard reads the flag, tells you if it&rsquo;s worth appealing,
                        and writes a ready-to-submit appeal &mdash; tailored using real creator outcomes.
                      </p>
                    </div>
                  </div>
                  <Link to="/free-violation-appeal" className="w-full sm:w-auto shrink-0" data-testid="hero-violation-cta">
                    <Button variant="premium" size="lg" className="w-full sm:w-auto whitespace-nowrap">
                      Try the Violation Wizard
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
                <p className="font-mono text-[11px] text-muted-foreground mt-3">
                  Free · no account needed · its own page you can share
                </p>
              </div>
            </div>

            {/* Right: live generation mock (hidden — replaced by live transcriber above) */}
            <div
              className="hidden"
              data-testid="hero-demo-card"
            >
              <div className="flex justify-between items-center px-4 py-3 border-b border-border font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                <span>Product · Ooono Co-Driver No1</span>
                <span className={heroDone ? 'text-primary' : 'text-accent'}>
                  {heroDone ? '\u2713 4 hooks' : 'Generating\u2026'}
                </span>
              </div>
              <div className="p-4 flex flex-col gap-2.5 min-h-[280px]">
                {heroLines.map((l, i) => (
                  <div
                    key={i}
                    className="rounded-lg bg-background/50 border border-border py-3 px-3.5 text-sm sm:text-[15px] font-semibold leading-snug"
                    style={{ borderLeft: `3px solid hsl(var(--${i % 2 ? 'accent' : 'primary'}))` }}
                  >
                    <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mr-2">
                      Hook {i + 1}
                    </span>
                    {l}
                    {!heroDone && i === heroLines.length - 1 && (
                      <span className="text-primary animate-pulse">▌</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ═══ FIVE NEW TOOLKIT SECTIONS ════════════════════════════════════ */}
      <HomeToolkitSections registerHref={registerHref} />

      {/* ═══ VIRAL TRANSCRIBER ════════════════════════════════════════════ */}
      <section>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className={eyebrowClass}>Viral Script Transcriber</p>
              <HeadingXL className="mt-3" accent="just went viral">
                Steal the structure of videos that
              </HeadingXL>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mt-5 mb-6">
                Paste any TikTok URL. We grab the transcript and use the pacing,
                structure and tone of that viral video as the blueprint for your
                own script. Stop guessing what works — mirror what&rsquo;s working right now.
              </p>
              <ul className="space-y-3 mb-7">
                {[
                  ['Paste & play', 'drop a TikTok link, we transcribe it instantly'],
                  ['Same hook formula, your product', 'the winning pattern applied to your niche'],
                  ['Free on every plan', 'including the 3-day trial'],
                ].map(([t, d]) => (
                  <li key={t} className="flex gap-3 text-sm sm:text-[15px]">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                    <span>
                      <strong className="font-semibold">{t}</strong>
                      <span className="text-muted-foreground"> — {d}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <Link to={registerHref}>
                <Button variant="premium" size="lg" data-testid="transcriber-cta">
                  Try it free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Transcriber UI mock */}
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-3">
                Viral Inspiration (optional)
              </p>
              <div className="flex gap-2 mb-4">
                <div className="flex-1 rounded-lg bg-background/60 border border-border px-3 py-3 text-xs sm:text-sm text-muted-foreground truncate">
                  tiktok.com/@viralcreator/video/…
                </div>
                <button
                  type="button"
                  aria-label="Fetch transcript (illustrative)"
                  className="rounded-lg bg-accent text-accent-foreground font-semibold px-4 text-sm hover:brightness-110 transition"
                >
                  Fetch
                </button>
              </div>
              <div className="rounded-lg border border-primary/30 bg-primary/[0.06] p-4 text-sm leading-relaxed italic">
                <p className="not-italic font-mono text-[10px] uppercase tracking-wider text-primary mb-2 flex items-center gap-1.5">
                  <Check className="w-3 h-3" /> Inspiration locked in
                </p>
                &ldquo;Okay so I bought this thing because everyone on my FYP was raving
                about it, and let me tell you it has completely changed my morning routine…&rdquo;
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS (four-step) ═════════════════════════════════════ */}
      <HowItWorks />

      {/* ═══ PRICING ══════════════════════════════════════════════════════ */}
      <section id="pricing" data-testid="home-pricing">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className={eyebrowClass}>Plans &amp; pricing</p>
            <HeadingXL className="mt-3">Start free. Upgrade when it pays for itself.</HeadingXL>
            {!lockedRegion && (
              <div className="flex flex-col items-center gap-2 mt-5">
                <CurrencyToggle />
                <PriceShownNote className="text-center" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {plans.map((p) => (
              <div
                key={p.key}
                className={`relative rounded-2xl border bg-card p-6 sm:p-7 flex flex-col ${p.hot ? 'border-accent shadow-glow-accent' : 'border-border'}`}
                data-testid={`plan-card-${p.key}`}
              >
                {p.hot && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full">
                    Most popular
                  </div>
                )}
                <h3 className="font-display font-extrabold text-xl mb-1">{p.name}</h3>
                <div className="mb-5">
                  <span className="font-display font-black text-4xl">{p.priceLabel}</span>
                  <span className="text-muted-foreground text-sm"> / {p.per}</span>
                </div>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {p.feats.map((f) => (
                    <li key={f} className="flex gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.hot ? 'text-accent' : 'text-primary'}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to={p.href} className="w-full">
                  <Button
                    variant={p.hot ? 'premium' : 'outline'}
                    size="lg"
                    className="w-full"
                    data-testid={`plan-cta-${p.key}`}
                  >
                    {p.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-muted-foreground text-xs mt-6">
            Cancel anytime · {lockedRegion === 'us' ? 'Prices in USD, billed monthly.' : lockedRegion === 'uk' ? 'Prices in GBP, billed monthly.' : 'Billing in GBP; USD toggle for display.'}
          </p>
        </div>
      </section>

      {/* ═══ FAQ ══════════════════════════════════════════════════════════ */}
      <section className="border-y border-primary/15 bg-primary/[0.05]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <HeadingXL className="mb-8">Questions creators ask</HeadingXL>
          {[
            [`How does Poettik work?`, `Paste a link to a TikTok or Instagram video that's already selling. Poettik pulls the transcript and breaks down what makes it work: the hook, the pacing, the tone and the persuasion. It then rewrites that structure for your product, in UGC, authority or negative style, and can turn the result into a voiceover. Developed by a team of experienced content creators with millions of views and GMV in sales between them. They set the guidelines for how every script is structured, from the first three seconds to the call to action, so you're writing the way proven creators do, not the way a generic AI thinks you should.`],
            [`Can't I just get my AI to do it?`, `No. ChatGPT starts from a blank prompt. Poettik starts from real videos that are already selling: it pulls their transcripts, breaks down the hook, pacing and persuasion across them, then writes your script to that proven structure.`],
            [`Won't I just be copying someone else's video?`, `No. Poettik copies the thinking, not the words. It borrows the structure that made the video work, like how it opens, when it shows proof and how it closes, and writes new lines for your product in your chosen style.`],
            [`Where do I find a winning video to paste?`, `Start with videos already selling your product, or something similar. Check the product's page in TikTok Shop, or search your niche and look for videos with high views and lots of "where did you get this?" comments. Add a few to a product and Poettik finds what they have in common.`],
            [`What if the video has no talking in it?`, `Poettik works best with videos that have spoken audio, because that's where the script comes from. For music-only or text-on-screen videos, you can write a script from your product details instead.`],
            [`Do I need to be a TikTok Shop affiliate?`, `No. It works for affiliates, UGC creators making content for brands, and sellers promoting their own products, on both TikTok and Instagram Reels.`],
            [`Can I turn my script into a voiceover?`, `Yes. Once your script is written, create a voiceover in one click — choose from a range of natural-sounding AI voices and download it as an MP3, ready to drop straight under your footage.`],
            [`What happens after the 3-day trial?`, `No card is taken upfront, so nothing is charged automatically. When your 3 days are up, just pick the plan that suits you to carry on generating — and you keep everything you made during the trial. Paid plans can be cancelled anytime from your account settings in a couple of clicks.`],
          ].map(([q, a]) => (
            <details key={q} className="border-b border-border py-4 group [&_summary::-webkit-details-marker]:hidden">
              <summary className="flex justify-between items-center gap-4 cursor-pointer font-semibold text-base sm:text-lg">
                {q}
                <span className="text-primary text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-muted-foreground text-sm sm:text-[15px] leading-relaxed pt-3">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ═══ FINAL CTA ════════════════════════════════════════════════════ */}
      <section>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="rounded-3xl border border-border bg-[hsl(var(--panel-2))] p-8 sm:p-14 text-center">
            <HeadingXL accent="you need Poettik." className="mb-4">
              If you&rsquo;re a creator,
            </HeadingXL>
            <p className="text-muted-foreground max-w-md mx-auto mb-6 leading-relaxed">
              Paste in a product. Get hooks, angles and a script ready to film. Free for 3 days.
            </p>
            <Link to={registerHref}>
              <Button variant="premium" size="xl" data-testid="home-final-cta">
                <Sparkles className="w-5 h-5 mr-2" />
                Start your free trial
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
